/**
 * v-customDrag 自定义拖拽指令 (优化完整版)
 * 功能特点：
 * 1. 支持 left/top 和 right/bottom 定位方式
 * 2. 自动处理定位方式转换
 * 3. 精确边界控制
 * 4. 支持动态尺寸元素
 * 使用方式：
 *   - v-customDrag 默认拖拽整个元素
 *   - v-customDrag="{ handle: '.header', target: '.dialog' }" 指定拖拽手柄和目标
 */
const dragDirective = {
  bind(el, binding) {
    initDrag(el, binding.value)
  },
  update(el, binding) {
    if (binding.oldValue !== binding.value) {
      initDrag(el, binding.value)
    }
  },
  componentUpdated(el, binding) {
    initDrag(el, binding.value)
  }
}

function initDrag(el, options = {}) {
  // 清除之前的监听器
  if (el._dragHandle && el._dragHandle._dragMouseDown) {
    el._dragHandle.removeEventListener('mousedown', el._dragHandle._dragMouseDown)
  }

  const config = typeof options === 'object' ? options : {}
  const handleSelector = config.handle || null
  const targetSelector = config.target || el

  // 获取拖拽手柄和目标元素
  const dragHandle = handleSelector ? el.querySelector(handleSelector) : el
  const dragTarget = targetSelector === el ? el : el.querySelector(targetSelector)

  if (!dragHandle || !dragTarget) {
    console.warn('v-customDrag: 无法找到指定的拖拽手柄或目标元素')
    return
  }

  // 确保目标元素有定位
  if (getComputedStyle(dragTarget).position === 'static') {
    dragTarget.style.position = 'fixed'
  }

  // 设置手柄样式
  dragHandle.style.cursor = 'move'

  const mouseDownHandler = (e) => {
    // 只响应左键点击
    if (e.button !== 0) return
    
    e.preventDefault()
    e.stopPropagation()

    let hasMoved = false
    const style = getComputedStyle(dragTarget)
    const rect = dragTarget.getBoundingClientRect()
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight
    const targetWidth = rect.width
    const targetHeight = rect.height

    // 计算初始位置（兼容所有定位方式）
    let initialX = rect.left
    let initialY = rect.top

    // 处理 right/bottom 定位
    if (style.left === 'auto' && style.right !== 'auto') {
      initialX = screenWidth - targetWidth - parseFloat(style.right)
    }
    if (style.top === 'auto' && style.bottom !== 'auto') {
      initialY = screenHeight - targetHeight - parseFloat(style.bottom)
    }

    const startX = e.clientX
    const startY = e.clientY

    // 添加拖拽效果
    dragTarget.style.transition = 'none'
    dragTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)'

    const mouseMoveHandler = (e) => {
      e.preventDefault()
      e.stopPropagation()

      // 计算移动距离
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        hasMoved = true
      }
      // 计算新位置
      let newLeft = initialX + dx
      let newTop = initialY + dy

      // 边界检查
      newLeft = Math.max(0, Math.min(newLeft, screenWidth - targetWidth))
      newTop = Math.max(0, Math.min(newTop, screenHeight - targetHeight))

      // 应用新位置（统一使用 left/top）
      dragTarget.style.left = `${newLeft}px`
      dragTarget.style.top = `${newTop}px`
      dragTarget.style.right = 'auto'
      dragTarget.style.bottom = 'auto'
    }

    const mouseUpHandler = (e) => {
      document.removeEventListener('mousemove', mouseMoveHandler)
      document.removeEventListener('mouseup', mouseUpHandler)
      
      // 恢复样式
      dragTarget.style.transition = ''
      dragTarget.style.boxShadow = ''
      if (hasMoved) {
        const rect = dragTarget.getBoundingClientRect()
        const screenWidth = window.innerWidth
        // 距离右侧一定距离自动吸附
        if (rect.left + rect.width + 15 >= screenWidth) {
          dragTarget.style.left = 'auto'
          dragTarget.style.right = '0'
        }
      }
    }

    document.addEventListener('mousemove', mouseMoveHandler)
    document.addEventListener('mouseup', mouseUpHandler)
  }

  // 存储引用以便后续清理
  dragHandle._dragMouseDown = mouseDownHandler
  dragHandle.addEventListener('mousedown', mouseDownHandler)
  el._dragHandle = dragHandle
  el._dragTarget = dragTarget
}

export default {
  install(Vue) {
    Vue.directive('customDrag', dragDirective)
  }
}